import React, { useEffect, useRef, useState } from 'react';

interface MinimapItem {
  x: number;
  y: number;
  w: number;
  h: number;
  color?: string;
  outlined?: boolean; // show as outline (no fill) — for callout/note boxes
}

interface MinimapOverlayProps {
  items: MinimapItem[];
  panOffset: { x: number; y: number };
  zoom: number;
  containerRef: React.RefObject<HTMLElement | SVGSVGElement>;
  onPanChange: (newPan: { x: number; y: number }) => void;
}

const MW = 180;
const MH = 120;
const PAD = 8;

// Memoized so a FlowchartBuilder re-render triggered by something unrelated to box
// geometry (selecting a box, hovering, editing text, opening a menu, ...) doesn't force
// this component to re-scan every box's bounds and re-render up to ~100+ SVG rects for
// no visible change. Only pays off if the caller also passes a referentially-stable
// `items` array (see FlowchartBuilder.tsx's minimapItems, fingerprinted the same way as
// useGanttSchedule's arrowBoxes) — panOffset/zoom/onPanChange were already stable.
const MinimapOverlay = React.memo(function MinimapOverlay({ items, panOffset, zoom, containerRef, onPanChange }: MinimapOverlayProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [containerSize, setContainerSize] = useState({ w: 800, h: 600 });
  const isDragging = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setContainerSize({ w: r.width, h: r.height });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef]);

  if (items.length === 0) return null;

  const { w: cW, h: cH } = containerSize;

  // Viewport bounds in canvas coords
  const vpL = -panOffset.x / zoom;
  const vpT = -panOffset.y / zoom;
  const vpR = (cW - panOffset.x) / zoom;
  const vpB = (cH - panOffset.y) / zoom;

  // Content bounds (nodes only — the viewport isn't factored in, so the scale
  // doesn't change when panning toward the center)
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const item of items) {
    if (item.x < minX) minX = item.x;
    if (item.y < minY) minY = item.y;
    if (item.x + item.w > maxX) maxX = item.x + item.w;
    if (item.y + item.h > maxY) maxY = item.y + item.h;
  }

  const contentW = maxX - minX || 1;
  const contentH = maxY - minY || 1;
  const scale = Math.min((MW - 2 * PAD) / contentW, (MH - 2 * PAD) / contentH);

  // Center within minimap
  const ox = PAD + ((MW - 2 * PAD) - contentW * scale) / 2;
  const oy = PAD + ((MH - 2 * PAD) - contentH * scale) / 2;

  const toMini = (cx: number, cy: number) => ({
    mx: (cx - minX) * scale + ox,
    my: (cy - minY) * scale + oy,
  });

  const vpTL = toMini(vpL, vpT);
  const vpMW = Math.max(4, (vpR - vpL) * scale);
  const vpMH = Math.max(4, (vpB - vpT) * scale);

  const navigateTo = (clientX: number, clientY: number) => {
    const rect = svgRef.current!.getBoundingClientRect();
    const mx = clientX - rect.left;
    const my = clientY - rect.top;
    const cx = (mx - ox) / scale + minX;
    const cy = (my - oy) / scale + minY;
    onPanChange({
      x: cW / 2 - cx * zoom,
      y: cH / 2 - cy * zoom,
    });
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    isDragging.current = true;
    navigateTo(e.clientX, e.clientY);

    const onMove = (ev: MouseEvent) => {
      if (isDragging.current) navigateTo(ev.clientX, ev.clientY);
    };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 16,
        left: 16,
        zIndex: 50,
        background: 'rgba(255,255,255,0.93)',
        border: '1px solid #d1d5db',
        borderRadius: 8,
        boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
        overflow: 'hidden',
        width: MW,
        height: MH,
        pointerEvents: 'auto',
      }}
    >
      <svg
        ref={svgRef}
        width={MW}
        height={MH}
        onMouseDown={handleMouseDown}
        style={{ cursor: 'crosshair', display: 'block' }}
      >
        {/* Content items */}
        {items.map((item, i) => {
          const { mx, my } = toMini(item.x, item.y);
          const color = item.color || '#6366f1';
          return item.outlined ? (
            <rect
              key={i}
              x={mx + 0.5}
              y={my + 0.5}
              width={Math.max(2, item.w * scale) - 1}
              height={Math.max(2, item.h * scale) - 1}
              fill="rgba(255,255,255,0.6)"
              stroke={color}
              strokeWidth={0.75}
              strokeDasharray="2 1"
              opacity={0.5}
              rx={1}
            />
          ) : (
            <rect
              key={i}
              x={mx}
              y={my}
              width={Math.max(2, item.w * scale)}
              height={Math.max(2, item.h * scale)}
              fill={color}
              opacity={0.65}
              rx={1}
            />
          );
        })}

        {/* Viewport rectangle */}
        <rect
          x={vpTL.mx}
          y={vpTL.my}
          width={vpMW}
          height={vpMH}
          fill="rgba(59,130,246,0.08)"
          stroke="#3b82f6"
          strokeWidth={1.5}
          rx={2}
          style={{ pointerEvents: 'none' }}
        />
      </svg>
    </div>
  );
});

export default MinimapOverlay;
